import './polyfill-process';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';

import App from './App.tsx';
import './index.css';
import { UnifiedErrorBoundary } from './components/ui/error-boundary';
import { createOptimizedQueryClient } from './config/queryClient.ts';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { ThemeProvider } from './contexts/ThemeContext.tsx';
import { installConsoleFilter } from './utils/consoleFilter';
import { setupGlobalErrorHandling } from './utils/cspPolicy';

import { NetworkManager } from '@/components/providers/NetworkManager';
import { persistOptions } from '@/config/queryClient';
import { adoptViewerReportingZone } from '@/lib/analytics/paid-reporting-window';
import './utils/devAuthHelpers';

// All imports must be above this line for React SWC plugin

adoptViewerReportingZone();

// CRITICAL FIX: Make React available globally to prevent createElement errors
// This ensures React is available for any vendor modules that might need it
// Specifically fixes Phosphor Icons v2 which uses React.createElement in ES modules
// Also fixes focus-trap-react which tries to set React.Children
if (typeof window !== 'undefined') {
  // Create a proper React object if needed
  if (!window.React) {
    window.React = React;
  }

  // Ensure React.Children is available
  if (!window.React.Children) {
    window.React.Children = React.Children;
  }

  // Make ReactDOM available
  window.ReactDOM = { createRoot };

  // Double-check React.createElement is available
  if (!React.createElement) {
    // Critical error - log to help debug React setup issues
    console.error('CRITICAL: React.createElement is not available!');
  }

  // React setup validation complete
}

// Install console filter early to prevent Sentry logging noise
installConsoleFilter();

// Disable console logs in production (except errors/warnings)
if (import.meta.env.PROD) {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  // Keep console.warn and console.error for critical issues
}

// Main module execution tracking
window.__mainTsxExecuted = true;

// Clean up any existing service workers that might interfere with auth
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then(function (registrations) {
      for (const registration of registrations) {
        if (import.meta.env.DEV) {
          // Unregistering existing service worker
        }
        registration.unregister();
      }
    })
    .catch((err) => {
      console.warn('Failed to unregister service workers:', err);
    });

  // Clear all caches that might contain corrupted service worker files
  if ('caches' in window) {
    caches
      .keys()
      .then(function (names) {
        for (const name of names) {
          if (import.meta.env.DEV) {
            // Deleting cache
          }
          caches.delete(name);
        }
      })
      .catch((err) => {
        console.warn('Failed to clear caches:', err);
      });
  }
}

// Initialize production error monitoring
setupGlobalErrorHandling();

// Handle Vite chunk preload errors (e.g. after a new deployment).
// Guard with a 10s sessionStorage cooldown to prevent infinite reload loops:
// without this, a consistently-failing chunk fires → reload → still fails →
// reload → infinite flash/reload cycle visible as "multiple colors flashing".
window.addEventListener('vite:preloadError', (event) => {
  const RELOAD_KEY = 'vite-preload-error-ts';
  const COOLDOWN_MS = 10_000;
  const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || '0');
  if (Date.now() - lastReload > COOLDOWN_MS) {
    console.warn('Vite preload error — reloading once to pick up latest chunks', event);
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();
  } else {
    console.warn('Vite preload error — cooldown active, not reloading again', event);
  }
});

// Temporarily disable global hook protection - too aggressive in development
// installGlobalHookProtection();

// Create optimized QueryClient with intelligent caching and retry logic
const queryClient = createOptimizedQueryClient();
// TEMP-VERIFY-HANDLE — remove before commit
(window as unknown as Record<string, unknown>).__qc = queryClient;

// Make queryClient globally accessible for error recovery
if (typeof window !== 'undefined') {
  (window as any).queryClient = queryClient;
}

// Query cache configured with optimized settings

// Emergency fallback component for critical React failures
const EmergencyFallback = () => {
  return React.createElement(
    'div',
    {
      style: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        fontFamily: 'system-ui, sans-serif',
        backgroundColor: 'hsl(var(--background, 250 250 250))',
        color: 'hsl(var(--foreground, 10 10 10))',
      },
    },
    [
      React.createElement(
        'h1',
        { key: 'title', style: { marginBottom: '1rem' } },
        '⚠️ Critical App Error',
      ),
      React.createElement(
        'p',
        { key: 'message', style: { marginBottom: '2rem', textAlign: 'center' } },
        'The app encountered a critical React error. Please refresh the page.',
      ),
      React.createElement(
        'button',
        {
          key: 'refresh',
          onClick: () => window.location.reload(),
          style: {
            padding: '0.75rem 1.5rem',
            backgroundColor: 'hsl(var(--foreground, 10 10 10))',
            color: 'hsl(var(--background, 250 250 250))',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
          },
        },
        'Refresh Page',
      ),
    ],
  );
};

// Clear any existing content in the root element before mounting the app
const rootElement = document.getElementById('root');
if (rootElement) {
  // Ensure the root element is empty
  rootElement.innerHTML = '';

  // NOTE: do NOT set document.title here. index.html ships the canonical
  // pre-JS title (SEO/og fallback) and each route owns its title via
  // usePageTitle / react-helmet-async ("<Page> · Mora"). Setting an
  // intermediate string here caused a visible tab-title FLASH on every load
  // (index.html title -> this string -> route title).

  // Add global error handlers for better error reporting
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    if (event.error?.message?.includes('useState')) {
      console.error('React hook error detected - possible React instance corruption');
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    // Enrich Sentry's auto-captured rejections. Default capture logs
    // "Unhandled promise rejection" with no stack or reason when the thrown
    // value isn't an Error instance (e.g. a raw string, null, or fetch
    // response object). Wrap it into a proper Error so Sentry's grouping and
    // breadcrumbs have something to work with.
    const reason = event.reason;
    const normalized =
      reason instanceof Error
        ? reason
        : new Error(
            typeof reason === 'string'
              ? `Unhandled promise rejection: ${reason}`
              : `Unhandled promise rejection: ${JSON.stringify(reason) ?? 'unknown'}`,
          );
    if (!(reason instanceof Error)) {
      (normalized as Error & { cause?: unknown }).cause = reason;
    }
    console.error('[unhandledrejection]', normalized);
  });

  // Safe provider mounting with emergency fallback and dynamic React Query loading
  try {
    // Test React hooks before creating providers
    const TestComponent = () => {
      React.useEffect(() => {}, []); // Test useEffect
      return null;
    };

    // Create a test root to validate React hooks work
    const testElement = document.createElement('div');
    const testRoot = createRoot(testElement);
    testRoot.render(React.createElement(TestComponent));
    testRoot.unmount();

    if (import.meta.env.DEV) {
      // React hooks validation passed - proceeding with app mount
    }

    // Dynamically import QueryClientProvider to ensure React is fully loaded first
    const _initializeApp = async () => {
      try {
        // Verify React is available and working
        if (!React || !React.createContext) {
          throw new Error('React is not properly loaded');
        }

        // Dynamically import React Query after React is confirmed to be working
        const { PersistQueryClientProvider } = await import('@tanstack/react-query-persist-client');
        const { NetworkManager } = await import('@/components/providers/NetworkManager');
        const { persistOptions } = await import('@/config/queryClient');

        // If hooks work, proceed with normal app mounting
        createRoot(rootElement).render(
          React.createElement(
            React.StrictMode,
            null,
            React.createElement(
              UnifiedErrorBoundary,
              {
                level: 'app',
                onError: (error, errorInfo) => {
                  // Enhanced error reporting for React hook failures
                  console.group('🚨 App-Level Error');
                  console.error('Error:', error);
                  console.error('Component Stack:', errorInfo.componentStack);

                  // Check for React hook corruption
                  if (
                    error?.message?.includes('useEffect') ||
                    error?.message?.includes('Invalid hook call')
                  ) {
                    console.error(
                      '🚨 CRITICAL: React hook corruption detected - app requires emergency restart',
                    );
                    // Force emergency fallback
                    setTimeout(() => {
                      createRoot(rootElement).render(React.createElement(EmergencyFallback));
                    }, 100);
                  }

                  // Send to Sentry only in production
                  if (import.meta.env.PROD) {
                    const sendToSentry = async () => {
                      const Sentry = await import('@sentry/react');
                      Sentry.captureException(error, {
                        contexts: {
                          react: {
                            componentStack: errorInfo.componentStack,
                            hookCorruption:
                              error?.message?.includes('useEffect') ||
                              error?.message?.includes('Invalid hook call'),
                          },
                        },
                      });
                    };
                    sendToSentry();
                  }
                  console.groupEnd();
                },
                fallback: React.createElement(EmergencyFallback),
              },
              React.createElement(
                HelmetProvider,
                null,
                React.createElement(
                  PersistQueryClientProvider,
                  {
                    client: queryClient,
                    persistOptions,
                  },
                  React.createElement(
                    ThemeProvider,
                    null,
                    React.createElement(
                      AuthProvider,
                      null,
                      React.createElement(NetworkManager, null, React.createElement(App)),
                    ),
                  ),
                ),
              ),
            ),
          ),
        );
      } catch (reactQueryError) {
        console.error('Failed to load React Query:', reactQueryError);
        // Mount emergency fallback
        createRoot(rootElement).render(React.createElement(EmergencyFallback));
      }
    };

    // initializeApp();

    // Cache the root across Vite HMR module reloads. Without this guard, every
    // edit to main.tsx (or its imports) re-runs createRoot on the same #root
    // element and React logs "createRoot() on a container that has already been
    // passed to createRoot()" 20+ times per save. Production has no HMR so this
    // path runs exactly once.
    const __w = window as unknown as {
      __moraRoot?: ReturnType<typeof createRoot>;
    };
    if (!__w.__moraRoot) {
      // Sentry init is deferred off the boot path (see initSentry() below), so
      // Sentry.reactErrorHandler isn't available synchronously here. The
      // app-level UnifiedErrorBoundary captures component render errors to
      // Sentry via a dynamic import; these root handlers cover React's
      // uncaught/recoverable paths with console (kept in prod — see the
      // console.log/debug/info noop block above, which preserves warn/error).
      __w.__moraRoot = createRoot(rootElement, {
        onUncaughtError: (error, errorInfo) => {
          console.error('Uncaught error', error, errorInfo.componentStack);
        },
        onCaughtError: (error, errorInfo) => {
          console.error('Caught error', error, errorInfo.componentStack);
        },
        onRecoverableError: (error, errorInfo) => {
          console.error('Recoverable error', error, errorInfo.componentStack);
        },
      });
    }

    __w.__moraRoot.render(
      React.createElement(
        React.StrictMode,
        null,
        React.createElement(
          UnifiedErrorBoundary,
          {
            level: 'app',
            fallback: React.createElement(EmergencyFallback),
          },
          React.createElement(
            HelmetProvider,
            null,
            React.createElement(
              PersistQueryClientProvider,
              {
                client: queryClient,
                persistOptions,
              },
              React.createElement(
                ThemeProvider,
                null,
                React.createElement(
                  AuthProvider,
                  null,
                  React.createElement(NetworkManager, null, React.createElement(App)),
                ),
              ),
            ),
          ),
        ),
      ),
    );

    // Defer Sentry init off the critical boot path. requestIdleCallback runs it
    // after first paint, so @sentry/react (incl. the heavy replay integration)
    // is a lazy chunk fetched post-paint instead of bloating the entry. No-op
    // in dev (initSentry early-returns when not PROD, so the SDK never loads).
    const __idle: (cb: () => void) => void =
      typeof window.requestIdleCallback === 'function'
        ? (cb) => window.requestIdleCallback(cb)
        : (cb) => window.setTimeout(cb, 1);
    __idle(() => {
      void import('./instrument').then((m) => m.initSentry());
    });
  } catch (hookError) {
    console.error('🚨 CRITICAL: React hooks failed validation - mounting emergency fallback');
    console.error('Hook Error:', hookError);

    // Mount emergency fallback using createElement to avoid JSX
    createRoot(rootElement).render(React.createElement(EmergencyFallback));
  }
} else {
  console.error('Root element not found! The app cannot be mounted.');
}
// QA marker 1776660506
